import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Clock, Trash2, ChevronRight, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { useChatSessions } from '../../contexts/ChatContext';
import { ChatSession } from '../../entities';
import { Drawer } from './Drawer';

interface SessionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SessionsDrawer: React.FC<SessionsDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { sessions, currentSessionId, selectSession, deleteSession } = useChatSessions();
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sort sessions by most recent first
  const sortedSessions = React.useMemo(() => {
    return [...sessions].sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [sessions]);

  const formatSessionTime = (date: string) => {
    const now = new Date();
    const sessionDate = new Date(date);
    const diffMs = now.getTime() - sessionDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return sessionDate.toLocaleDateString();
  };

  const handleSessionClick = (session: ChatSession) => {
    selectSession(session.id);
    navigate('/chat');
    onClose();
  };

  const handleDeleteClick = (sessionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSessionToDelete(sessionId);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteSession(sessionToDelete);
      setSessionToDelete(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setSessionToDelete(null);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      title="All Sessions"
    >
      <div className="flex flex-col h-full">
        {/* Sessions List - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {sortedSessions.length === 0 ? (
            <div className="py-12 text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-40 text-rm-neutral-600" />
              <p className="text-sm text-rm-neutral-600">No sessions yet</p>
              <p className="text-xs text-rm-neutral-600 mt-1">
                Start a conversation to see it here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedSessions.map((session) => {
                const isActive = session.id === currentSessionId;
                const isMarkedForDelete = session.id === sessionToDelete;
                
                return (
                  <div
                    key={session.id}
                    className={clsx(
                      'rounded-lg border transition-all',
                      isActive 
                        ? 'border-rm-accent-500 bg-rm-accent-50' 
                        : 'border-rm-neutral-600/20 bg-white',
                      isMarkedForDelete && 'opacity-50'
                    )}
                  >
                    <button
                      onClick={() => handleSessionClick(session)}
                      disabled={isMarkedForDelete}
                      className={clsx(
                        'w-full flex items-start px-4 py-3 text-left transition-colors',
                        'touch-target',
                        !isMarkedForDelete && !isActive && 'hover:bg-rm-bg-50'
                      )}
                    >
                      <MessageCircle 
                        className={clsx(
                          'w-5 h-5 mt-0.5 mr-3 flex-shrink-0',
                          isActive ? 'text-rm-accent-600' : 'text-rm-neutral-600'
                        )} 
                      />
                      <div className="flex-1 min-w-0">
                        <p className={clsx(
                          'text-sm font-medium truncate',
                          isActive ? 'text-rm-accent-700' : 'text-rm-ink-900'
                        )}>
                          {session.title || 'New Conversation'}
                        </p>
                        <div className="flex items-center mt-1 text-xs text-rm-neutral-600">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatSessionTime(session.updatedAt)}
                        </div>
                      </div>
                      {isActive && (
                        <ChevronRight className="w-5 h-5 text-rm-accent-600 flex-shrink-0 ml-2" />
                      )}
                    </button>

                    {/* Delete Button */}
                    <div className="border-t border-rm-neutral-600/10 px-4 py-2">
                      <button
                        onClick={(e) => handleDeleteClick(session.id, e)}
                        disabled={isMarkedForDelete}
                        className={clsx(
                          'w-full flex items-center justify-center gap-2 px-3 py-2',
                          'text-sm font-medium rounded-md transition-colors touch-target',
                          isMarkedForDelete
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'text-red-600 hover:bg-red-50'
                        )}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Session
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {sessionToDelete && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-rm-ink-900 mb-1">
                    Delete Session
                  </h3>
                  <p className="text-sm text-rm-neutral-600">
                    Are you sure you want to delete this session? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className={clsx(
                    'flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors',
                    'border border-rm-neutral-600/20 text-rm-ink-900 hover:bg-rm-bg-50',
                    isDeleting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className={clsx(
                    'flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors',
                    'bg-red-600 text-white hover:bg-red-700',
                    isDeleting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};
